<template>
    <div class="space-y-6">
        <div class="flex items-center justify-between">
            <h2 class="text-heading-md">Date Picker Examples</h2>
            <div class="flex items-center gap-4">
                <span class="text-content-md font-medium text-content-secondary">Size:</span>
                <DsRadio
                    v-model="size"
                    label="Medium (default)"
                    name="date-picker-size"
                    value="md"
                />
                <DsRadio
                    v-model="size"
                    label="Small"
                    name="date-picker-size"
                    value="sm"
                />
            </div>
        </div>

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div>
                <h5 class="mb-1 text-content-lg font-semibold">Base date picker</h5>
                <div class="mb-3 text-content-md text-content-secondary">Selected date: {{ baseDatePicker }}</div>
                <DsCheckbox
                    v-model="toggleError"
                    class="my-4"
                    label="Toggle error state"
                    name="toggleError"
                    value="An error has occurred"
                />
                <DsDatePicker
                    v-model="baseDatePicker"
                    :input="{
                        placeholder: 'Select date',
                        error: toggleError ? 'An error has occurred' : undefined,
                        label: 'Date',
                        labelTooltip: 'Info icon tooltip',
                        required: true,
                        size,
                    }"
                />
            </div>

            <div>
                <h5 class="mb-1 text-content-lg font-semibold">Standalone calendar</h5>
                <div class="mb-3 text-content-md text-content-secondary">Selected date: {{ standaloneDatePicker }}</div>

                <DsDatePicker
                    v-model="standaloneDatePicker"
                    v-model:openPicker="isCalendarPopupOpen"
                    :input="{ placeholder: 'Select date', error: toggleError, size }"
                    standaloneCalendar
                />
            </div>

            <div>
                <h5 class="mb-1 text-content-lg font-semibold">With custom trigger</h5>
                <div class="mb-3 text-content-md text-content-secondary">Selected date: {{ customTriggerDatePicker }}</div>

                <DsDatePicker
                    v-model="customTriggerDatePicker"
                    :closeOnSelect="false"
                    :input="{ placeholder: 'Select date', size }"
                >
                    <template #trigger>
                        <DsButton
                            size="md"
                            variant="secondary"
                        >
                            Open calendar
                            <small>(Anything can be here)</small>
                        </DsButton>
                    </template>
                </DsDatePicker>
            </div>

            <div>
                <h5 class="mb-1 text-content-lg font-semibold">With some dates disabled</h5>
                <div class="mb-3 text-content-md text-content-secondary">Selected date: {{ disabledDatesDatePicker }}</div>

                <DsDatePicker
                    v-model="disabledDatesDatePicker"
                    v-model:openPicker="isDisabledDatesCalendarPopupOpen"
                    :input="{ placeholder: 'Select date', error: toggleError, size }"
                    :maxValue="createCalendarDateFromJS(dayjs().add(7, 'day').toDate())"
                    :minValue="createToday()"
                    standaloneCalendar
                />
            </div>

            <div>
                <h5 class="mb-1 text-content-lg font-semibold">With warning</h5>
                <DsDatePicker
                    v-model="warningDatePicker"
                    :input="{ placeholder: 'Select date', label: 'Date', warning: 'Warning message', size }"
                />
            </div>

            <div>
                <h5 class="mb-1 text-content-lg font-semibold">With error</h5>
                <DsDatePicker
                    v-model="errorDatePicker"
                    :input="{ placeholder: 'Select date', label: 'Date', error: 'This date is invalid', size }"
                />
            </div>

            <div>
                <h5 class="mb-1 text-content-lg font-semibold">With helper text</h5>
                <DsDatePicker
                    v-model="helperDatePicker"
                    :input="{ placeholder: 'Select date', label: 'Date', helperText: 'Pick a closing date', size }"
                />
            </div>

            <div>
                <h5 class="mb-1 text-content-lg font-semibold">Disabled</h5>
                <DsDatePicker
                    v-model="disabledDatePicker"
                    disabled
                    :input="{ placeholder: 'Select date', label: 'Date', size }"
                />
            </div>

            <div>
                <h5 class="mb-1 text-content-lg font-semibold">Custom label tooltip slot</h5>
                <DsDatePicker
                    v-model="customTooltipDatePicker"
                    :input="{ placeholder: 'Select date', label: 'Date', size }"
                >
                    <template #labelTooltip>
                        <div>
                            Custom tooltip with
                            <strong>rich content</strong>
                        </div>
                    </template>
                </DsDatePicker>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsButton from '@/components/design-system/button/DsButton.vue';
import DsDatePicker from '@/components/design-system/form/date-picker/DsDatePicker.vue';
import DsCheckbox from '@/components/design-system/form/input/DsCheckbox.vue';
import { createCalendarDateFromJS, createDateValueRef, createToday } from '@/shared/utils/calendar-date-utils';
import DsRadio from '@ds/form/input/DsRadio.vue';
import type { DsInputSize } from '@ds/form/input/text/text-input.types';
import dayjs from 'dayjs';
import { ref } from 'vue';

const size = ref<DsInputSize>('md');

const isCalendarPopupOpen = ref(false);
const isDisabledDatesCalendarPopupOpen = ref(false);
const toggleError = ref(false);

const baseDatePicker = createDateValueRef(createCalendarDateFromJS(dayjs().subtract(1, 'day').toDate()));
const standaloneDatePicker = createDateValueRef(createToday());
const customTriggerDatePicker = createDateValueRef(createCalendarDateFromJS(dayjs().subtract(1, 'day').toDate()));
const disabledDatesDatePicker = createDateValueRef(createToday());
const warningDatePicker = createDateValueRef(createCalendarDateFromJS(dayjs().subtract(1, 'day').toDate()));
const errorDatePicker = createDateValueRef(createCalendarDateFromJS(dayjs().subtract(1, 'day').toDate()));
const helperDatePicker = createDateValueRef(createCalendarDateFromJS(dayjs().subtract(1, 'day').toDate()));
const disabledDatePicker = createDateValueRef(createCalendarDateFromJS(dayjs().subtract(1, 'day').toDate()));
const customTooltipDatePicker = createDateValueRef(createCalendarDateFromJS(dayjs().subtract(1, 'day').toDate()));
</script>
